import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { IndianRupee, Percent, Calendar, Search, ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, FileText, Info, Users, Clock, TrendingUp, HandHeart, Sparkles, Calculator } from "lucide-react";
import { VerticalHeader, VerticalFooter, VERTICAL_THEMES } from "./BrandChrome";
import "./asg.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated Unsplash imagery for finance & aspirational visuals
const IMG_HERO = "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1600&q=80";
const IMG_HOME = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80";
const IMG_FAMILY = "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=800&q=80";
const IMG_BUSINESS = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80";
const IMG_EDU = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80";
const IMG_CAR = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80";
const IMG_PROP = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80";
const IMG_PERSONAL = "https://images.unsplash.com/photo-1579621970590-9d624316904b?auto=format&fit=crop&w=800&q=80";

const IMG_BY_SLUG = {
  "personal-loan": IMG_PERSONAL,
  "home-loan": IMG_HOME,
  "business-loan": IMG_BUSINESS,
  "education-loan": IMG_EDU,
  "vehicle-loan": IMG_CAR,
  "loan-against-property": IMG_PROP,
};

const LOAN_TYPES = [
  "Personal Loan", "Home Loan", "Business Loan", "Education Loan",
  "Vehicle Loan", "Loan Against Property", "Other"
];

const AMOUNT_RANGES = [
  { value: "1L-5L", label: "₹1 Lakh – ₹5 Lakh" },
  { value: "5L-25L", label: "₹5 Lakh – ₹25 Lakh" },
  { value: "25L-1Cr", label: "₹25 Lakh – ₹1 Crore" },
  { value: "1Cr+", label: "₹1 Crore & above" },
];

export function LoansList() {
  const [loans, setLoans] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/loans`).then(r => setLoans(r.data || [])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(loans.map(l => l.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [loans]);

  const filtered = useMemo(() => {
    return loans.filter(l => {
      if (cat !== "all" && l.category !== cat) return false;
      if (q) {
        const s = q.toLowerCase();
        return (l.name || "").toLowerCase().includes(s)
          || (l.short_description || "").toLowerCase().includes(s)
          || (l.tagline || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [loans, q, cat]);

  return (
    <div className="asg-shell asg-vertical-loans" data-testid="loans-page">
      <Helmet>
        <title>Loans by Arth Setuu Global — Personal, Home, Business, Education</title>
        <meta name="description" content="Personal, home, business, education, vehicle and property loan assistance across India. Transparent rates, doorstep documentation and fast approval — 24 to 72 hours." />
        <link rel="canonical" href="https://arthsetuuglobal.in/loans" />
      </Helmet>

      <VerticalHeader theme={VERTICAL_THEMES.loans} currentVertical="loans" />

      <section className="asg-vhero asg-vhero-image" style={{ backgroundImage: `url(${IMG_HERO})` }}>
        <div className="asg-vhero-inner">
          <span className="asg-vhero-eyebrow"><IndianRupee size={12} /> Arth Setuu · Loans</span>
          <h1>Finance made simple.<br/>Life made lighter.</h1>
          <p>Six loan categories, one trusted advisor. From ₹1 Lakh personal loans to ₹5 Crore home financing — transparent rates, doorstep documentation, and 24 to 72 hour approvals.</p>
          <div className="asg-vhero-stats">
            <div><b>₹200Cr+</b><span>Loans facilitated</span></div>
            <div><b>24–72 hrs</b><span>Approval window</span></div>
            <div><b>4.8★</b><span>Customer rating</span></div>
            <div><b>6</b><span>Loan products</span></div>
          </div>
        </div>
      </section>

      {/* Lead generation form (prominent, above the fold on scroll) */}
      <section className="asg-lead-band" data-testid="loan-lead-band">
        <div className="asg-lead-inner">
          <div className="asg-lead-copy">
            <span className="asg-hero-eyebrow" style={{ background: "#D1FAE5", color: "#053D28", borderColor: "transparent" }}>Free Eligibility Check</span>
            <h2>Get a free loan advisory call — in under 60 seconds.</h2>
            <p>Tell us a little about yourself and we'll match you to the right loan, share indicative rates and set up a callback within 24 hours. No commitment. No spam.</p>
            <ul className="asg-lead-benefits">
              <li><ShieldCheck size={16} /> 100% confidential — data never sold</li>
              <li><Clock size={16} /> Callback within 24 hours</li>
              <li><HandHeart size={16} /> Advisor who speaks your language</li>
              <li><TrendingUp size={16} /> Best-fit lender from 20+ banks & NBFCs</li>
            </ul>
          </div>
          <LeadForm />
        </div>
      </section>

      <div className="asg-container">
        <div className="asg-filters" data-testid="loans-filters">
          <div className="asg-search-input">
            <Search size={16} color="#64748B" />
            <input
              placeholder="Search loan types…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="loans-search-input"
            />
          </div>
          {categories.map(c => (
            <button
              key={c}
              className={`asg-chip ${cat === c ? "active" : ""}`}
              onClick={() => setCat(c)}
              data-testid={`loans-chip-${c}`}
            >
              {c === "all" ? "All loans" : c}
            </button>
          ))}
        </div>

        <div className="asg-section-title">
          <div>
            <h2>Choose the loan that fits your life</h2>
            <p>Compare across categories, then book a free eligibility call.</p>
          </div>
        </div>

        {loading ? (
          <div className="asg-empty">Loading loans…</div>
        ) : filtered.length === 0 ? (
          <div className="asg-empty" data-testid="loans-empty">
            <b>No products match this search</b>
            Try different keywords or clear the filters.
          </div>
        ) : (
          <div className="asg-grid" data-testid="loans-grid">
            {filtered.map(l => (
              <div key={l.slug} className="asg-card asg-loan-card" data-testid={`loan-card-${l.slug}`}>
                <div className="asg-card-image" style={{ backgroundImage: `url(${IMG_BY_SLUG[l.slug] || IMG_HERO})` }}>
                  <span className="asg-card-tag" style={{ position: "absolute", left: 16, top: 16 }}>{l.category}</span>
                </div>
                <div className="asg-loan-card-body">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 26 }}>{l.icon}</span> {l.name}
                  </h3>
                  <p>{l.short_description || l.tagline}</p>
                  <div className="asg-card-meta">
                    <span><Percent size={13} /> {l.interest_range}</span>
                    <span><Calendar size={13} /> {l.tenure_range}</span>
                  </div>
                  <div className="asg-card-meta" style={{ marginBottom: 20 }}>
                    <span style={{ color: "#0F7A4E", fontWeight: 700 }}><IndianRupee size={13} /> {l.max_amount}</span>
                  </div>
                  <Link to={`/loans/${l.slug}`} className="asg-card-link" data-testid={`loan-view-${l.slug}`}>
                    View details & apply <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <ProcessSection />
        <TrustSection />
      </div>

      <VerticalFooter theme={VERTICAL_THEMES.loans} />
    </div>
  );
}

function ProcessSection() {
  const steps = [
    { n: "01", t: "Share your details", d: "Fill the 60-second enquiry form or WhatsApp us. Include loan type, amount and any specific need." },
    { n: "02", t: "Free eligibility call", d: "Our advisor calls within 24 hours to verify basics and shortlist the best-fit lenders for your profile." },
    { n: "03", t: "Doorstep documents", d: "We collect KYC, income and property papers from your home or office — across Patna and Bihar." },
    { n: "04", t: "Approval & disbursal", d: "Once the lender approves, funds are credited to your account. Typical timeline: 24–72 hours after documentation." },
  ];
  return (
    <section className="asg-process" data-testid="loans-process">
      <div className="asg-section-title">
        <div>
          <h2>How our loan advisory works</h2>
          <p>Four simple steps — from enquiry to disbursal — with a human advisor at every stage.</p>
        </div>
      </div>
      <div className="asg-process-grid">
        {steps.map(s => (
          <div key={s.n} className="asg-process-card">
            <span className="asg-process-num">{s.n}</span>
            <h4>{s.t}</h4>
            <p>{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrustSection() {
  const items = [
    { icon: <ShieldCheck size={22} />, t: "20+ Banks & NBFCs", d: "We compare offers across leading lenders so you get the best rate for your profile." },
    { icon: <HandHeart size={22} />, t: "Human-first advisory", d: "No IVR mazes. Real advisors on WhatsApp and phone, in Hindi and English." },
    { icon: <Sparkles size={22} />, t: "Transparent, always", d: "Every fee, rate and clause explained in plain language — before you sign." },
    { icon: <Users size={22} />, t: "3,500+ served", d: "Trusted by families, business owners and students across Bihar and beyond." },
  ];
  return (
    <section className="asg-trust" data-testid="loans-trust">
      <div className="asg-trust-grid">
        {items.map((it, i) => (
          <div key={i} className="asg-trust-card">
            <span className="asg-trust-icon">{it.icon}</span>
            <b>{it.t}</b>
            <p>{it.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function LeadForm() {
  const [form, setForm] = useState({
    name: "", mobile: "", age: "", occupation: "", occupation_other: "",
    loan_type: "", loan_amount_range: "", email: "", city: "", consent: false
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    if (!form.name || !form.mobile) { setErr("Name and mobile are required."); return; }
    if (form.occupation === "Others" && !form.occupation_other.trim()) { setErr("Please specify your occupation."); return; }
    const payload = { ...form, age: form.age ? parseInt(form.age, 10) : null };
    setBusy(true);
    try {
      await axios.post(`${API}/lead-enquiry`, payload);
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (ok) return (
    <div className="asg-card asg-lead-form-card" data-testid="lead-form-success">
      <div className="asg-form-success">
        <div className="asg-check"><CheckCircle2 size={30} /></div>
        <h4>Enquiry received</h4>
        <p>Thanks {form.name.split(" ")[0]} — our loan advisor will call you within 24 hours to discuss your <b>{form.loan_type || "loan"}</b> requirement of <b>{AMOUNT_RANGES.find(a => a.value === form.loan_amount_range)?.label || "your budget"}</b>.</p>
      </div>
    </div>
  );

  return (
    <div className="asg-card asg-lead-form-card">
      <h3 className="asg-form-title">Book free consultation</h3>
      <p className="asg-form-sub">A loan advisor will call you within 24 hours. No obligation.</p>
      <form onSubmit={submit} data-testid="lead-form">
        {err && <div className="asg-form-error" data-testid="lead-form-error">{err}</div>}
        <div className="asg-form-row">
          <div className="asg-form-group">
            <label>Full name *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="lead-input-name" />
          </div>
          <div className="asg-form-group">
            <label>Age</label>
            <input type="number" min="18" max="80" value={form.age} onChange={e => set("age", e.target.value)} data-testid="lead-input-age" />
          </div>
        </div>
        <div className="asg-form-row">
          <div className="asg-form-group">
            <label>Mobile *</label>
            <input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="lead-input-mobile" />
          </div>
          <div className="asg-form-group">
            <label>City</label>
            <input value={form.city} onChange={e => set("city", e.target.value)} data-testid="lead-input-city" />
          </div>
        </div>
        <div className="asg-form-group">
          <label>Occupation</label>
          <select value={form.occupation} onChange={e => set("occupation", e.target.value)} data-testid="lead-input-occupation">
            <option value="">Select…</option>
            <option value="Govt Job">Govt Job</option>
            <option value="Pvt Job">Private Job</option>
            <option value="Business">Business</option>
            <option value="Others">Others</option>
          </select>
        </div>
        {form.occupation === "Others" && (
          <div className="asg-form-group" data-testid="lead-occupation-other-wrap">
            <label>Please specify</label>
            <input
              value={form.occupation_other}
              onChange={e => set("occupation_other", e.target.value)}
              placeholder="e.g. Freelancer, Student, Homemaker"
              data-testid="lead-input-occupation-other"
            />
          </div>
        )}
        <div className="asg-form-group">
          <label>Loan type</label>
          <select value={form.loan_type} onChange={e => set("loan_type", e.target.value)} data-testid="lead-input-loan-type">
            <option value="">Select loan type…</option>
            {LOAN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="asg-form-group">
          <label>Loan amount</label>
          <select value={form.loan_amount_range} onChange={e => set("loan_amount_range", e.target.value)} data-testid="lead-input-loan-amount">
            <option value="">Select amount range…</option>
            {AMOUNT_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="asg-form-group">
          <label>Email (optional)</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="lead-input-email" />
        </div>
        <label className="asg-form-consent">
          <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} data-testid="lead-input-consent" />
          <span>I consent to Arth Setuu Global contacting me via call/WhatsApp/email for loan advisory.</span>
        </label>
        <button className="asg-btn-primary" type="submit" disabled={busy} data-testid="lead-form-submit">
          {busy ? "Submitting…" : <>Get free consultation <ArrowRight size={14} /></>}
        </button>
        <p className="asg-form-note"><ShieldCheck size={11} style={{marginRight:4,marginBottom:-1}} />100% confidential. We are a facilitator — final approvals rest with the lender.</p>
      </form>
    </div>
  );
}

export function LoanDetail() {
  const { slug } = useParams();
  const [loan, setLoan] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/loans/${slug}`)
      .then(r => setLoan(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="asg-shell asg-vertical-loans">
        <VerticalHeader theme={VERTICAL_THEMES.loans} currentVertical="loans" />
        <div className="asg-container">
          <div className="asg-empty"><b>Loan product not found</b>Please <Link to="/loans">view all loan products</Link>.</div>
        </div>
        <VerticalFooter theme={VERTICAL_THEMES.loans} />
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="asg-shell asg-vertical-loans">
        <VerticalHeader theme={VERTICAL_THEMES.loans} currentVertical="loans" />
        <div className="asg-container"><div className="asg-empty">Loading…</div></div>
      </div>
    );
  }

  const heroImg = IMG_BY_SLUG[loan.slug] || IMG_HERO;

  return (
    <div className="asg-shell asg-vertical-loans" data-testid="loan-detail-page">
      <Helmet>
        <title>{`${loan.name} — Arth Setuu Global`}</title>
        <meta name="description" content={loan.short_description || loan.tagline} />
        <link rel="canonical" href={`https://arthsetuuglobal.in/loans/${loan.slug}`} />
      </Helmet>

      <VerticalHeader theme={VERTICAL_THEMES.loans} currentVertical="loans" />

      <section className="asg-vhero asg-detail-hero asg-vhero-image" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="asg-vhero-inner">
          <Link to="/loans" className="asg-detail-hero-back"><ArrowLeft size={14} /> All loans</Link>
          <span className="asg-vhero-eyebrow">{loan.category}</span>
          <h1><span style={{ marginRight: 12, fontSize: '0.9em' }}>{loan.icon}</span>{loan.name}</h1>
          <p>{loan.tagline}</p>
        </div>
      </section>

      <div className="asg-container">
        <div className="asg-detail">
          <div className="asg-detail-main">
            <div className="asg-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14, marginBottom: 24 }}>
              <div className="asg-info-block"><h4>Interest</h4><b>{loan.interest_range}</b><br/><small>per annum</small></div>
              <div className="asg-info-block"><h4>Tenure</h4><b>{loan.tenure_range}</b></div>
              <div className="asg-info-block"><h4>Max amount</h4><b>{loan.max_amount}</b></div>
              <div className="asg-info-block"><h4>Processing fee</h4><b style={{fontSize:16}}>{loan.processing_fee}</b></div>
            </div>

            <EMICalculator loan={loan} />

            {loan.short_description && (
              <>
                <h2>Overview</h2>
                <p>{loan.short_description}</p>
              </>
            )}

            {loan.eligibility?.length > 0 && (
              <>
                <h2>Eligibility</h2>
                <ul>{loan.eligibility.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}

            {loan.documents?.length > 0 && (
              <>
                <h2><FileText size={18} style={{marginRight:6,marginBottom:-3}} />Documents required</h2>
                <ul>{loan.documents.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}

            {loan.features?.length > 0 && (
              <>
                <h2>Key features</h2>
                <ul>{loan.features.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}

            {loan.faqs?.length > 0 && (
              <>
                <h2>Frequently asked</h2>
                {loan.faqs.map((f, i) => (
                  <div key={i} style={{ background: "#fff", padding: 18, borderRadius: 12, border: "1px solid #E2E8F0", marginBottom: 10 }}>
                    <b style={{ display: "block", marginBottom: 6, fontSize: 14.5 }}><Info size={14} style={{marginRight:6,marginBottom:-2, color:'#0F7A4E'}}/>{f.q}</b>
                    <span style={{ color: "#475569", fontSize: 14, lineHeight: 1.6 }}>{f.a}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <aside className="asg-detail-side">
            <LoanEnquiryForm loan={loan} />
          </aside>
        </div>
      </div>

      <VerticalFooter theme={VERTICAL_THEMES.loans} />
    </div>
  );
}

function EMICalculator({ loan }) {
  // Parse interest range like "10.5% – 24% p.a." → [10.5, 24]
  const parseRange = (str, fallback = [10, 15]) => {
    const nums = (str || "").match(/\d+(\.\d+)?/g);
    if (!nums || nums.length === 0) return fallback;
    if (nums.length === 1) return [parseFloat(nums[0]), parseFloat(nums[0])];
    return [parseFloat(nums[0]), parseFloat(nums[1])];
  };
  // Parse max_amount like "Up to ₹25 Lakh" or "Up to ₹5 Crore" → rupees
  const parseAmount = (str, fallback = 2500000) => {
    if (!str) return fallback;
    const m = str.match(/(\d+(?:\.\d+)?)/);
    if (!m) return fallback;
    const n = parseFloat(m[1]);
    if (/crore|cr/i.test(str)) return Math.round(n * 10000000);
    if (/lakh|lac/i.test(str)) return Math.round(n * 100000);
    return Math.round(n);
  };
  // Parse tenure like "1 – 5 years" → months
  const parseTenure = (str, fallback = [12, 60]) => {
    const nums = (str || "").match(/\d+/g);
    if (!nums || nums.length === 0) return fallback;
    const isYears = /year|yr/i.test(str);
    const mult = isYears ? 12 : 1;
    if (nums.length === 1) return [parseInt(nums[0]) * mult, parseInt(nums[0]) * mult];
    return [parseInt(nums[0]) * mult, parseInt(nums[1]) * mult];
  };

  const [rMin, rMax] = parseRange(loan.interest_range);
  const [tMin, tMax] = parseTenure(loan.tenure_range);
  const amtMax = parseAmount(loan.max_amount);
  const amtMin = Math.max(100000, Math.floor(amtMax * 0.02));
  const stepAmt = amtMax > 5000000 ? 100000 : 25000;

  const defaultAmt = Math.min(500000, Math.max(amtMin, Math.round(amtMax * 0.2)));

  const [amount, setAmount] = useState(defaultAmt);
  const [tenure, setTenure] = useState(Math.min(60, Math.max(tMin, Math.round((tMin + tMax) / 2))));
  const [rate, setRate] = useState(Number(((rMin + rMax) / 2).toFixed(2)));

  const { emi, totalPay, totalInterest } = useMemo(() => {
    const P = amount;
    const r = rate / 12 / 100;
    const n = tenure;
    if (P <= 0 || n <= 0 || r <= 0) return { emi: 0, totalPay: 0, totalInterest: 0 };
    const pow = Math.pow(1 + r, n);
    const e = (P * r * pow) / (pow - 1);
    return {
      emi: Math.round(e),
      totalPay: Math.round(e * n),
      totalInterest: Math.round(e * n - P),
    };
  }, [amount, tenure, rate]);

  const fmt = (n) => "₹" + n.toLocaleString("en-IN");

  const scrollToEnquiry = () => {
    const el = document.querySelector('[data-testid="loan-apply-form"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const amtInput = el.querySelector('[data-testid="loan-input-amount"]');
      if (amtInput) {
        const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
        nativeSetter.call(amtInput, fmt(amount));
        amtInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  const pct = (v, min, max) => `${((v - min) / (max - min)) * 100}%`;

  return (
    <div className="asg-emi" data-testid="emi-calculator">
      <div className="asg-emi-head">
        <div>
          <h3><Calculator size={18} style={{ marginRight: 8, marginBottom: -3, color: 'var(--asg-chip-active)' }} />EMI Calculator</h3>
          <p>Move the sliders to see your monthly EMI, total interest and payable amount.</p>
        </div>
        <span className="asg-emi-head-badge">Instant · Free</span>
      </div>
      <div className="asg-emi-body">
        <div>
          <div className="asg-emi-slider">
            <div className="asg-emi-slider-head">
              <label>Loan amount</label>
              <b data-testid="emi-amount-value">{fmt(amount)}</b>
            </div>
            <input
              type="range"
              min={amtMin} max={amtMax} step={stepAmt}
              value={amount}
              onChange={e => setAmount(parseInt(e.target.value))}
              style={{ "--_p": pct(amount, amtMin, amtMax) }}
              data-testid="emi-amount-slider"
            />
            <div className="asg-emi-slider-legend">
              <span>{fmt(amtMin)}</span>
              <span>{fmt(amtMax)}</span>
            </div>
          </div>

          <div className="asg-emi-slider">
            <div className="asg-emi-slider-head">
              <label>Tenure</label>
              <b data-testid="emi-tenure-value">{tenure} {tenure === 1 ? "month" : "months"}{tenure >= 12 && ` · ${(tenure / 12).toFixed(tenure % 12 === 0 ? 0 : 1)} yr`}</b>
            </div>
            <input
              type="range"
              min={tMin} max={tMax} step={1}
              value={tenure}
              onChange={e => setTenure(parseInt(e.target.value))}
              style={{ "--_p": pct(tenure, tMin, tMax) }}
              data-testid="emi-tenure-slider"
            />
            <div className="asg-emi-slider-legend">
              <span>{tMin} mo</span>
              <span>{tMax} mo</span>
            </div>
          </div>

          <div className="asg-emi-slider" style={{ marginBottom: 0 }}>
            <div className="asg-emi-slider-head">
              <label>Interest rate</label>
              <b data-testid="emi-rate-value">{rate.toFixed(2)}% p.a.</b>
            </div>
            <input
              type="range"
              min={rMin} max={rMax === rMin ? rMin + 0.01 : rMax} step={0.1}
              value={rate}
              onChange={e => setRate(parseFloat(e.target.value))}
              style={{ "--_p": pct(rate, rMin, rMax === rMin ? rMin + 0.01 : rMax) }}
              data-testid="emi-rate-slider"
            />
            <div className="asg-emi-slider-legend">
              <span>{rMin}%</span>
              <span>{rMax}%</span>
            </div>
          </div>
        </div>

        <div className="asg-emi-result">
          <span className="asg-emi-result-eyebrow">Monthly EMI</span>
          <div className="asg-emi-result-amount" data-testid="emi-monthly">{fmt(emi)}</div>
          <p className="asg-emi-result-sub">every month for {tenure} {tenure === 1 ? "month" : "months"}</p>
          <div className="asg-emi-breakdown">
            <div className="asg-emi-breakdown-item">
              <small>Principal</small>
              <b data-testid="emi-principal">{fmt(amount)}</b>
            </div>
            <div className="asg-emi-breakdown-item">
              <small>Interest</small>
              <b data-testid="emi-interest">{fmt(totalInterest)}</b>
            </div>
            <div className="asg-emi-breakdown-item" style={{ gridColumn: "1 / -1", borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
              <small>Total payable</small>
              <b data-testid="emi-total" style={{ fontSize: 20 }}>{fmt(totalPay)}</b>
            </div>
          </div>
          <button className="asg-emi-cta" onClick={scrollToEnquiry} data-testid="emi-apply-cta">
            Apply for {fmt(amount)} <ArrowRight size={14} />
          </button>
        </div>
      </div>
      <p className="asg-emi-note">
        Indicative only. Actual EMI, rate and processing fee are set by the lender after credit assessment.
      </p>
    </div>
  );
}

function LoanEnquiryForm({ loan }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "",
    loan_amount: "", monthly_income: "", occupation: "",
    message: "", consent: false
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    if (!form.name || !form.mobile) { setErr("Name and mobile are required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/loans/${loan.slug}/apply`, form);
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (ok) return (
    <div className="asg-card">
      <div className="asg-form-success" data-testid="loan-apply-success">
        <div className="asg-check"><CheckCircle2 size={30} /></div>
        <h4>Enquiry received</h4>
        <p>Thanks {form.name.split(" ")[0]} — our loan advisor will call you within 24 hours to discuss <b>{loan.name}</b> and share the next steps.</p>
      </div>
    </div>
  );

  return (
    <div className="asg-card">
      <h3 className="asg-form-title">Get free loan advisory</h3>
      <p className="asg-form-sub">Speak with a loan advisor within 24 hours — no obligation.</p>
      <form onSubmit={submit} data-testid="loan-apply-form">
        {err && <div className="asg-form-error" data-testid="loan-apply-error">{err}</div>}
        <div className="asg-form-group">
          <label>Full name *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="loan-input-name" />
        </div>
        <div className="asg-form-group">
          <label>Mobile *</label>
          <input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="loan-input-mobile" />
        </div>
        <div className="asg-form-group">
          <label>Email</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="loan-input-email" />
        </div>
        <div className="asg-form-group">
          <label>City</label>
          <input value={form.city} onChange={e => set("city", e.target.value)} data-testid="loan-input-city" />
        </div>
        <div className="asg-form-group">
          <label>Loan amount required</label>
          <input placeholder="e.g. ₹5,00,000" value={form.loan_amount} onChange={e => set("loan_amount", e.target.value)} data-testid="loan-input-amount" />
        </div>
        <div className="asg-form-group">
          <label>Monthly income</label>
          <input placeholder="e.g. ₹40,000" value={form.monthly_income} onChange={e => set("monthly_income", e.target.value)} data-testid="loan-input-income" />
        </div>
        <div className="asg-form-group">
          <label>Occupation</label>
          <select value={form.occupation} onChange={e => set("occupation", e.target.value)} data-testid="loan-input-occupation">
            <option value="">Select…</option>
            <option>Salaried</option>
            <option>Self-employed</option>
            <option>Business owner</option>
            <option>Student</option>
            <option>Other</option>
          </select>
        </div>
        <div className="asg-form-group">
          <label>Message</label>
          <textarea placeholder="Anything we should know?" value={form.message} onChange={e => set("message", e.target.value)} data-testid="loan-input-message" />
        </div>
        <label className="asg-form-consent">
          <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} data-testid="loan-input-consent" />
          <span>I consent to Arth Setuu Global contacting me for loan advisory via call/WhatsApp/email.</span>
        </label>
        <button className="asg-btn-primary" type="submit" disabled={busy} data-testid="loan-apply-submit">
          {busy ? "Submitting…" : <>Get free consultation <ArrowRight size={14} /></>}
        </button>
        <p className="asg-form-note"><ShieldCheck size={11} style={{marginRight:4,marginBottom:-1}} />We are a facilitator — final approvals rest with the lender.</p>
      </form>
    </div>
  );
}
